import {
  SUPABASE_OPENAI_FUNCTION,
  isSupabaseConfigured,
  supabase,
  supabaseAnonKey,
  supabaseUrl,
} from "../supabase";

function requireSupabase() {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error(
      "Supabase is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY."
    );
  }
  return supabase;
}

function maybeThrow(error) {
  if (error) {
    throw error;
  }
}

function noop() {}

async function getFunctionErrorMessage(error) {
  if (!error) return "Edge Function request failed.";
  const fallback = error.message || "Edge Function request failed.";

  const response = error.context;
  if (!response || typeof response.clone !== "function") {
    return fallback;
  }

  try {
    const cloned = response.clone();
    const contentType = cloned.headers?.get?.("content-type") || "";

    if (contentType.includes("application/json")) {
      const body = await cloned.json();
      return body?.error || body?.message || fallback;
    }

    const text = (await cloned.text())?.trim();
    return text || fallback;
  } catch {
    return fallback;
  }
}

async function getCurrentAccessToken(client) {
  const {
    data: { session },
    error,
  } = await client.auth.getSession();

  if (error) {
    throw error;
  }

  return session?.access_token || null;
}

async function getReadyAccessToken(client) {
  let token = await getCurrentAccessToken(client);
  if (token) return token;

  const { data, error } = await client.auth.refreshSession();
  if (error) {
    return null;
  }

  token = data?.session?.access_token || null;
  return token || null;
}

async function invokeEdgeFunctionWithUserToken(functionName, body, token) {
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error("Supabase function transport is not configured.");
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/${functionName}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: supabaseAnonKey,
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json")
    ? await response.json()
    : { message: (await response.text())?.trim() || "" };

  if (!response.ok) {
    throw new Error(
      payload?.error || payload?.message || `Edge Function request failed with ${response.status}.`
    );
  }

  return payload;
}

export function normalizeAuthUser(user) {
  if (!user) return null;
  const authDisplayName = String(
    user.user_metadata?.user_name ||
      user.user_metadata?.full_name ||
      user.user_metadata?.name ||
      ""
  ).trim();

  return {
    uid: user.id,
    email: user.email || "",
    displayName: authDisplayName,
    photoURL: user.user_metadata?.avatar_url || "",
    rawUser: user,
  };
}

export async function getCurrentAuthUser() {
  const client = requireSupabase();
  const { data, error } = await client.auth.getUser();
  maybeThrow(error);
  return normalizeAuthUser(data.user);
}

export function onSupabaseAuthChange(callback) {
  if (!supabase) {
    callback(null);
    return () => {};
  }

  const {
    data: { subscription },
  } = supabase.auth.onAuthStateChange((_event, session) => {
    callback(normalizeAuthUser(session?.user || null));
  });

  return () => subscription.unsubscribe();
}

export async function signInWithPassword(email, password) {
  const client = requireSupabase();
  const { data, error } = await client.auth.signInWithPassword({
    email,
    password,
  });
  maybeThrow(error);
  return data;
}

export async function signUpWithPassword(email, password, username) {
  const client = requireSupabase();
  const { data, error } = await client.auth.signUp({
    email,
    password,
    options: {
      data: {
        user_name: String(username || "").trim(),
      },
    },
  });
  maybeThrow(error);
  return data;
}

export async function signOutUser() {
  const client = requireSupabase();
  const { error } = await client.auth.signOut();
  maybeThrow(error);
}

function mapProfileRow(row) {
  if (!row) return null;
  return {
    displayName: row.display_name || "",
    photoURL: row.photo_url || "",
    totalXP: Number(row.total_xp || 0),
    level: Number(row.level || 1),
    currentTitle: row.current_title || "Weakest Hunter of Mankind",
    autoBossQuest: !!row.auto_boss_quest,
    lastLevelUpPopupLevel: Number(row.last_level_up_popup_level || 0),
    lastLevelUpShownAt: row.last_level_up_shown_at || null,
    wakeTime: row.wake_time || "",
    sleepTime: row.sleep_time || "",
    timeZone: row.timezone || "UTC",
    longTermGoal: row.long_term_goal || "",
    hunterProfile: row.hunter_profile || "",
    questsPerDay: Math.max(1, Number(row.quests_per_day || 5)),
    preferredFocusMinutes: Number.isFinite(Number(row.preferred_focus_minutes))
      ? Number(row.preferred_focus_minutes)
      : null,
    nonNegotiables: row.non_negotiables || "",
    frictionPoints: row.friction_points || "",
    onboardingCompleted: row.onboarding_completed !== false,
    createdAt: row.created_at || null,
    updatedAt: row.updated_at || null,
  };
}

function profilePayload(payload = {}) {
  const next = {};
  if ("displayName" in payload) next.display_name = payload.displayName || "";
  if ("photoURL" in payload) next.photo_url = payload.photoURL || "";
  if ("totalXP" in payload) next.total_xp = Number(payload.totalXP || 0);
  if ("level" in payload) next.level = Number(payload.level || 1);
  if ("currentTitle" in payload) next.current_title = payload.currentTitle || "Weakest Hunter of Mankind";
  if ("autoBossQuest" in payload) next.auto_boss_quest = !!payload.autoBossQuest;
  if ("lastLevelUpPopupLevel" in payload) next.last_level_up_popup_level = Number(payload.lastLevelUpPopupLevel || 0);
  if ("lastLevelUpShownAt" in payload) next.last_level_up_shown_at = payload.lastLevelUpShownAt || null;
  if ("wakeTime" in payload) next.wake_time = payload.wakeTime || "";
  if ("sleepTime" in payload) next.sleep_time = payload.sleepTime || "";
  if ("timeZone" in payload) next.timezone = payload.timeZone || "UTC";
  if ("longTermGoal" in payload) next.long_term_goal = payload.longTermGoal || "";
  if ("hunterProfile" in payload) next.hunter_profile = payload.hunterProfile || "";
  if ("questsPerDay" in payload) {
    const parsed = Number(payload.questsPerDay || 0);
    next.quests_per_day = Number.isFinite(parsed) ? Math.max(1, Math.min(10, Math.round(parsed))) : 5;
  }
  if ("preferredFocusMinutes" in payload) {
    next.preferred_focus_minutes =
      Number.isFinite(Number(payload.preferredFocusMinutes)) && Number(payload.preferredFocusMinutes) > 0
        ? Number(payload.preferredFocusMinutes)
        : null;
  }
  if ("nonNegotiables" in payload) next.non_negotiables = payload.nonNegotiables || "";
  if ("frictionPoints" in payload) next.friction_points = payload.frictionPoints || "";
  if ("onboardingCompleted" in payload) next.onboarding_completed = !!payload.onboardingCompleted;
  if ("createdAt" in payload) next.created_at = payload.createdAt;
  return next;
}

export async function getProfile(uid) {
  const client = requireSupabase();
  const { data, error } = await client.from("profiles").select("*").eq("id", uid).maybeSingle();
  maybeThrow(error);
  return mapProfileRow(data);
}

export async function upsertProfile(uid, payload) {
  const client = requireSupabase();
  const { data, error } = await client
    .from("profiles")
    .upsert({ id: uid, ...profilePayload(payload) }, { onConflict: "id" })
    .select("*")
    .single();
  maybeThrow(error);
  return mapProfileRow(data);
}

export function subscribeToProfile(uid, callback) {
  if (!supabase) {
    callback(null);
    return () => {};
  }

  let active = true;
  getProfile(uid)
    .then((profile) => {
      if (active) callback(profile);
    })
    .catch((error) => console.error("Failed to fetch profile:", error));

  const channel = supabase
    .channel(`profile:${uid}`)
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "profiles",
        filter: `id=eq.${uid}`,
      },
      (payload) => {
        callback(mapProfileRow(payload.new || null));
      }
    )
    .subscribe();

  return () => {
    active = false;
    supabase.removeChannel(channel).catch(noop);
  };
}

export async function getDailyQuestRow(uid, dateStr) {
  const client = requireSupabase();
  const { data, error } = await client
    .from("daily_quests")
    .select("quests, created_at, updated_at")
    .eq("user_id", uid)
    .eq("quest_date", dateStr)
    .maybeSingle();
  maybeThrow(error);
  return data
    ? {
        quests: Array.isArray(data.quests) ? data.quests : [],
        createdAt: data.created_at || null,
        updatedAt: data.updated_at || null,
      }
    : null;
}

export async function upsertDailyQuestRow(uid, dateStr, quests, createdAt = null) {
  const client = requireSupabase();
  const payload = {
    user_id: uid,
    quest_date: dateStr,
    quests,
  };

  if (createdAt) {
    payload.created_at = createdAt;
  }

  const { data, error } = await client
    .from("daily_quests")
    .upsert(payload, { onConflict: "user_id,quest_date" })
    .select("quests, created_at, updated_at")
    .single();
  maybeThrow(error);
  return {
    quests: Array.isArray(data.quests) ? data.quests : [],
    createdAt: data.created_at || null,
    updatedAt: data.updated_at || null,
  };
}

export async function getDailyQuestRowsInRange(uid, fromDate, toDate) {
  const client = requireSupabase();
  const { data, error } = await client
    .from("daily_quests")
    .select("quest_date, quests")
    .eq("user_id", uid)
    .gte("quest_date", fromDate)
    .lte("quest_date", toDate)
    .order("quest_date", { ascending: true });
  maybeThrow(error);
  return data || [];
}

export async function getFixedQuests(uid) {
  const client = requireSupabase();
  const { data, error } = await client
    .from("fixed_quests")
    .select("quest_id, quest")
    .eq("user_id", uid)
    .order("created_at", { ascending: true });
  maybeThrow(error);
  return (data || []).map(({ quest_id: questId, quest }) => ({
    ...(quest || {}),
    id: questId,
  }));
}

export async function upsertFixedQuest(uid, questId, quest) {
  const client = requireSupabase();
  const { error } = await client.from("fixed_quests").upsert(
    {
      user_id: uid,
      quest_id: questId,
      quest: { ...quest, id: questId },
    },
    { onConflict: "user_id,quest_id" }
  );
  maybeThrow(error);
}

export async function deleteFixedQuest(uid, questId) {
  const client = requireSupabase();
  const { error } = await client
    .from("fixed_quests")
    .delete()
    .eq("user_id", uid)
    .eq("quest_id", questId);
  maybeThrow(error);
}

export async function getPenaltyQuests(uid, dateStr) {
  const client = requireSupabase();
  const { data, error } = await client
    .from("penalty_quests")
    .select("quest_id, quest")
    .eq("user_id", uid)
    .eq("quest_date", dateStr)
    .order("created_at", { ascending: true });
  maybeThrow(error);
  return (data || []).map(({ quest_id: questId, quest }) => ({
    ...(quest || {}),
    id: questId,
  }));
}

export async function upsertPenaltyQuest(uid, dateStr, questId, quest) {
  const client = requireSupabase();
  const { error } = await client.from("penalty_quests").upsert(
    {
      user_id: uid,
      quest_date: dateStr,
      quest_id: questId,
      quest: { ...quest, id: questId },
    },
    { onConflict: "user_id,quest_date,quest_id" }
  );
  maybeThrow(error);
}

export async function deletePenaltyQuest(uid, dateStr, questId) {
  const client = requireSupabase();
  const { error } = await client
    .from("penalty_quests")
    .delete()
    .eq("user_id", uid)
    .eq("quest_date", dateStr)
    .eq("quest_id", questId);
  maybeThrow(error);
}

export async function getBossQuest(uid, weekId) {
  const client = requireSupabase();
  const { data, error } = await client
    .from("boss_quests")
    .select("quest, created_at, updated_at")
    .eq("user_id", uid)
    .eq("week_id", weekId)
    .maybeSingle();
  maybeThrow(error);
  return data
    ? {
        id: weekId,
        ...(data.quest || {}),
        createdAt: data.created_at || null,
        updatedAt: data.updated_at || null,
      }
    : null;
}

export async function upsertBossQuest(uid, weekId, quest) {
  const client = requireSupabase();
  const { data, error } = await client
    .from("boss_quests")
    .upsert(
      {
        user_id: uid,
        week_id: weekId,
        quest,
      },
      { onConflict: "user_id,week_id" }
    )
    .select("quest, created_at, updated_at")
    .single();
  maybeThrow(error);
  return {
    id: weekId,
    ...(data.quest || {}),
    createdAt: data.created_at || null,
    updatedAt: data.updated_at || null,
  };
}

export async function listBossQuests(uid) {
  const client = requireSupabase();
  const { data, error } = await client
    .from("boss_quests")
    .select("week_id, quest, created_at, updated_at")
    .eq("user_id", uid)
    .order("week_id", { ascending: true });
  maybeThrow(error);
  return (data || []).map((row) => ({
    id: row.week_id,
    ...(row.quest || {}),
    createdAt: row.created_at || null,
    updatedAt: row.updated_at || null,
  }));
}

export function subscribeToBossQuest(uid, weekId, callback) {
  if (!supabase) {
    callback(null);
    return () => {};
  }

  let active = true;
  getBossQuest(uid, weekId)
    .then((quest) => {
      if (active) callback(quest);
    })
    .catch((error) => console.error("Failed to fetch boss quest:", error));

  const channel = supabase
    .channel(`boss-quest:${uid}:${weekId}`)
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "boss_quests",
        filter: `user_id=eq.${uid}`,
      },
      (payload) => {
        const next = payload.new;
        if (!next || next.week_id !== weekId) return;
        callback({
          id: weekId,
          ...(next.quest || {}),
          createdAt: next.created_at || null,
          updatedAt: next.updated_at || null,
        });
      }
    )
    .subscribe();

  return () => {
    active = false;
    supabase.removeChannel(channel).catch(noop);
  };
}

function mapRewardRow(row) {
  return {
    id: row.id,
    title: row.title,
    desc: row.description || "",
    milestone: row.milestone || "",
    milestoneLevel: Number.isFinite(Number(row.milestone_level))
      ? Number(row.milestone_level)
      : null,
    icon: row.icon || "gift",
    claimed: !!row.claimed,
    claimedAt: row.claimed_at || null,
    createdAt: row.created_at || null,
    updatedAt: row.updated_at || null,
  };
}

export async function listRewards(uid) {
  const client = requireSupabase();
  const { data, error } = await client
    .from("rewards")
    .select("*")
    .eq("user_id", uid)
    .order("created_at", { ascending: false });
  maybeThrow(error);
  return (data || []).map(mapRewardRow);
}

export async function insertReward(uid, reward) {
  const client = requireSupabase();
  const { data, error } = await client
    .from("rewards")
    .insert({
      user_id: uid,
      title: reward.title,
      description: reward.desc || "",
      milestone: reward.milestone || "",
      milestone_level:
        Number.isFinite(Number(reward.milestoneLevel)) && Number(reward.milestoneLevel) > 0
          ? Number(reward.milestoneLevel)
          : null,
      icon: reward.icon || "gift",
      claimed: false,
    })
    .select("*")
    .single();
  maybeThrow(error);
  return mapRewardRow(data);
}

export async function updateReward(uid, id, updates) {
  const client = requireSupabase();
  const payload = {};
  if ("title" in updates) payload.title = updates.title;
  if ("desc" in updates) payload.description = updates.desc || "";
  if ("milestone" in updates) payload.milestone = updates.milestone || "";
  if ("milestoneLevel" in updates) {
    payload.milestone_level =
      Number.isFinite(Number(updates.milestoneLevel)) && Number(updates.milestoneLevel) > 0
        ? Number(updates.milestoneLevel)
        : null;
  }
  if ("icon" in updates) payload.icon = updates.icon || "gift";
  if ("claimed" in updates) payload.claimed = !!updates.claimed;
  if ("claimedAt" in updates) payload.claimed_at = updates.claimedAt || null;

  const { data, error } = await client
    .from("rewards")
    .update(payload)
    .eq("user_id", uid)
    .eq("id", id)
    .select("*")
    .single();
  maybeThrow(error);
  return mapRewardRow(data);
}

export async function removeReward(uid, id) {
  const client = requireSupabase();
  const { error } = await client.from("rewards").delete().eq("user_id", uid).eq("id", id);
  maybeThrow(error);
}

function mapXPHistoryRow(row) {
  return {
    id: row.id,
    questId: row.quest_id || null,
    date: row.event_date,
    xp: Number(row.xp || 0),
    isPenalty: !!row.is_penalty,
    createdAt: row.created_at || null,
    ...(row.metadata || {}),
  };
}

export async function listXPHistory(uid) {
  const client = requireSupabase();
  const { data, error } = await client
    .from("xp_history")
    .select("*")
    .eq("user_id", uid)
    .order("event_date", { ascending: true })
    .order("created_at", { ascending: true });
  maybeThrow(error);
  return (data || []).map(mapXPHistoryRow);
}

export async function insertXPHistory(uid, entry) {
  const client = requireSupabase();
  const { data, error } = await client
    .from("xp_history")
    .insert({
      user_id: uid,
      quest_id: entry.questId || null,
      event_date: entry.date,
      xp: Number(entry.xp || 0),
      is_penalty: !!entry.isPenalty,
      metadata: entry.metadata || {},
    })
    .select("*")
    .single();
  maybeThrow(error);
  return mapXPHistoryRow(data);
}

export async function getSystemAdjustment(uid, adjustmentId) {
  const client = requireSupabase();
  const { data, error } = await client
    .from("system_adjustments")
    .select("adjustment")
    .eq("user_id", uid)
    .eq("adjustment_id", adjustmentId)
    .maybeSingle();
  maybeThrow(error);
  return data?.adjustment || null;
}

export async function upsertSystemAdjustment(uid, adjustmentId, adjustment) {
  const client = requireSupabase();
  const { error } = await client.from("system_adjustments").upsert(
    {
      user_id: uid,
      adjustment_id: adjustmentId,
      adjustment,
    },
    { onConflict: "user_id,adjustment_id" }
  );
  maybeThrow(error);
}

export async function getSystemAward(uid, awardId) {
  const client = requireSupabase();
  const { data, error } = await client
    .from("system_awards")
    .select("award")
    .eq("user_id", uid)
    .eq("award_id", awardId)
    .maybeSingle();
  maybeThrow(error);
  return data?.award || null;
}

export async function upsertSystemAward(uid, awardId, award) {
  const client = requireSupabase();
  const { error } = await client.from("system_awards").upsert(
    {
      user_id: uid,
      award_id: awardId,
      award,
    },
    { onConflict: "user_id,award_id" }
  );
  maybeThrow(error);
}

export async function listProfilesForLeaderboard() {
  const client = requireSupabase();
  const { data, error } = await client
    .from("profiles")
    .select("id, display_name, photo_url, total_xp, level, current_title")
    .order("total_xp", { ascending: false })
    .order("level", { ascending: false })
    .order("display_name", { ascending: true });
  maybeThrow(error);
  return data || [];
}

export async function invokeOpenAIFunction(body) {
  const client = requireSupabase();
  let token = await getReadyAccessToken(client);
  if (!token) {
    throw new Error("System session is not ready yet.");
  }

  try {
    return await invokeEdgeFunctionWithUserToken(
      SUPABASE_OPENAI_FUNCTION,
      body,
      token
    );
  } catch (error) {
    const detailedMessage = await getFunctionErrorMessage(error);
    if (/invalid jwt/i.test(detailedMessage)) {
      const { data: refreshedData, error: refreshError } = await client.auth.refreshSession();
      if (!refreshError) {
        token = refreshedData?.session?.access_token || (await getCurrentAccessToken(client));
        if (token) {
          return invokeEdgeFunctionWithUserToken(
            SUPABASE_OPENAI_FUNCTION,
            body,
            token
          );
        }
      }
    }
    throw error;
  }
}
